/**
 * Reusable SQUARE (1:1) image cropper for profile photos. Opens a modal
 * with the picked image, drag + zoom (round preview to match the circular
 * avatar), and emits a cropped square JPEG File via onCropped. Mirrors the
 * seasonal-theme cropper UX so uploads land as a clean 512×512 headshot.
 */
import { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const OUTPUT = 512; // 512×512 square, matches server crop.

async function cropToFile(srcUrl, cropArea, sourceFileName) {
  const img = await new Promise((res, rej) => {
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => res(el);
    el.onerror = rej;
    el.src = srcUrl;
  });
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT;
  canvas.height = OUTPUT;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    img,
    cropArea.x, cropArea.y, cropArea.width, cropArea.height,
    0, 0, OUTPUT, OUTPUT,
  );
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
  if (!blob) throw new Error('Could not export cropped image');
  const cleanName = (sourceFileName || 'photo').replace(/\.[a-z0-9]+$/i, '');
  return new File([blob], `${cleanName}-square.jpg`, { type: 'image/jpeg' });
}

export default function ImageCropperSquare({
  file,
  onCropped,
  onCancel,
  title = 'Adjust photo',
}) {
  const [imgUrl, setImgUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const handleSave = async () => {
    if (!pixels || !imgUrl) return;
    setSaving(true);
    try {
      const cropped = await cropToFile(imgUrl, pixels, file?.name);
      await onCropped?.(cropped);
    } catch (err) {
      toast.error(err?.message || 'Could not crop image');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!file} onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent className="max-w-md" data-testid="photo-cropper-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative w-full h-[320px] bg-slate-900 rounded-lg overflow-hidden">
            {imgUrl && (
              <Cropper
                image={imgUrl}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                restrictPosition
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div>
            <Label className="text-xs">Zoom</Label>
            <input
              type="range" min={1} max={4} step={0.05} value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full" data-testid="photo-cropper-zoom"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Drag to reposition · pinch or use the slider to zoom · saved as a 512×512 square.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="photo-cropper-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !pixels} data-testid="photo-cropper-save">
            {saving && <Loader2 size={14} className="mr-1 animate-spin" />}
            Apply &amp; upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
